<script setup>
  import { computed, ref, watch } from "vue";
  import { storeToRefs } from "pinia";
  import { useSurveyOptions } from "@utils/composables/filters/useSurveyOptions";
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
  } from "@/components/ui/dialog";
  import { Input } from "@/components/ui/input";
  import { Select } from "@/components/episto-ui/select";
  import { Button } from "@/components/ui/button";
  import { Label } from "@/components/ui/label";
  import { useSurveysStore } from "@stores/surveys";
  import { useMCampaignStore } from "@stores/mCampaign";

  const { selectLightSurvey } = useSurveysStore();
  const { bindings: surveyOptionsBindings, load: loadSurveyOptions } = useSurveyOptions();

  const mCampaignStore = useMCampaignStore();
  const { currentMCampaign, isNewCampaignVisible } = storeToRefs(mCampaignStore);
  const { fetchNewCampaign, saveMCampaign, clearMCampaign } = mCampaignStore;

  const handleSurveySelect = (value) => {
    currentMCampaign.value.survey_id = value;
    selectLightSurvey(value);
  };

  const isCreating = ref(false);

  const disabled = computed(() => {
    return isCreating.value || !currentMCampaign.value?.name || !currentMCampaign.value?.survey_id;
  });

  const handleClose = () => {
    isNewCampaignVisible.value = false;
    clearMCampaign();
  };

  watch(isNewCampaignVisible, async (open) => {
    if (open) await Promise.all([loadSurveyOptions(1), fetchNewCampaign()]);
  });
  const createMCampaign = async () => {
    if (disabled.value) return;

    isCreating.value = true;
    try {
      await saveMCampaign();
      const token = currentMCampaign.value.token;
      handleClose();
      window.location.assign(`${window.location.origin}/admin/m_campaigns/${token}/setup`);
    } finally {
      isCreating.value = false;
    }
  };
</script>

<template>
  <Dialog
    :open="isNewCampaignVisible"
    @update:open="
      (val) => {
        if (!val) handleClose();
      }
    ">
    <DialogContent
      :show-close-button="false"
      class="font-sans sm:max-w-md">
      <DialogHeader>
        <DialogTitle>Create a new campaign</DialogTitle>
        <DialogDescription>Fill in the details to create a new campaign.</DialogDescription>
      </DialogHeader>

      <div
        v-if="currentMCampaign"
        class="w-full min-w-0 space-y-4">
        <div class="w-full min-w-0 space-y-2">
          <Label for="campaign-name">Name</Label>
          <Input
            id="campaign-name"
            v-model="currentMCampaign.name"
            placeholder="Campaign name" />
        </div>

        <div class="w-full min-w-0 max-w-full space-y-2">
          <Label for="campaign-survey">Survey</Label>
          <Select
            v-bind="surveyOptionsBindings"
            class="w-full min-w-0 max-w-full"
            :model-value="currentMCampaign.survey_id"
            placeholder="Search..."
            @update:model-value="handleSurveySelect" />
        </div>
      </div>

      <DialogFooter>
        <Button
          variant="outline"
          @click="handleClose">
          Cancel
        </Button>
        <Button
          :disabled="disabled"
          @click="createMCampaign">
          Create campaign
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
